/**
 * The "Basic" form variant is characterized by:
 *    - 3px rounded corners
 *    - 30px input height
 *    - 11px text
 *    - borders all around text inputs
 *    - solid blue primary buttons
 */

import React from 'react'
import * as stylex from '@web/lib/stylex'
import { asTrackedClickable } from '@web/o11y/analytics/tracked_clickable'
import { asTrackedInput } from '@web/o11y/analytics/tracked_input'
import { InputPopulationLevel } from '@web/o11y/analytics/tracking'
import infoIcon from '@web/ui-platform/images/info_small.svg'
import { Svg } from '@web/ui-platform/svg_deprecated'
import { getTooltipProps, TooltipType } from '@web/ui-platform/tooltip'
import classNames from 'classnames'

import { LazyInput__DEPRECATED } from 'shared/components/lazy_input/lazy_input'
import { sx_DEPRECATED } from 'shared/style_builder'

import * as styles from './basic_form.css'

export const WORKSPACE_AND_BILLING_GROUP_NAME_CHAR_LIMIT = 255
export const FOLDER_NAME_CHAR_LIMIT = 118

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  dataTestId?: string
  recordingKey?: string
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { className, ...props },
  ref,
) {
  return (
    <LazyInput__DEPRECATED
      className={classNames(styles.textInput, className)}
      {...props}
      ref={ref}
    />
  )
})

export function MockTextInput(props: TextInputProps) {
  return <input {...props} />
}

export class BigTextInput extends React.Component<React.HTMLProps<HTMLInputElement>, {}> {
  public static displayName = 'BigTextInput'

  public override render() {
    const className = `${styles.bigTextInput} ${this.props.className || ''}`
    const props = { ...this.props, className }
    return <LazyInput__DEPRECATED {...(props as any)} />
  }
}

const LazyInputTracked = asTrackedInput(LazyInput__DEPRECATED)

type LabeledTextInputProps = {
  value: string
  htmlName: string
  label: string
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onChange: (e: React.SyntheticEvent) => void
  onBlur?: (e: React.SyntheticEvent) => void
  onFocus?: (e: React.SyntheticEvent) => void
  placeholder?: string
  trackingFieldName?: string
  autoCompleteName?: string
  maxLength?: number
  disabled?: boolean
  className?: string
  type?: string
  dataTestId?: string
  required?: boolean
  tooltip?: string
  hasError?: boolean
  noMarginBottom?: boolean
}

export class LabeledTextInput extends React.Component<LabeledTextInputProps, {}> {
  public override render() {
    return (
      <div
        className={
          this.props.className ||
          (this.props.disabled ? styles.labeledInputGroupDisabled : styles.labeledInputGroup)
        }
        style={sx_DEPRECATED.if(this.props.noMarginBottom, { marginBottom: 0 }).$}
      >
        <label
          htmlFor={this.props.htmlName}
          className={classNames(styles.hoverLabel, {
            [styles.hoverLabel__hasValue]: this.props.value,
            [styles.error]: this.props.hasError,
          })}
        >
          {this.props.label}
        </label>
        {this.props.trackingFieldName && (
          <LazyInputTracked
            autoComplete={this.props.autoCompleteName}
            dataTestId={this.props.dataTestId}
            disabled={this.props.disabled}
            id={this.props.htmlName}
            maxLength={this.props.maxLength || 50}
            name={this.props.htmlName}
            onBlur={this.props.onBlur}
            onChange={this.props.onChange}
            onFocus={this.props.onFocus}
            onKeyPress={this.props.onKeyPress}
            placeholder={this.props.placeholder || this.props.label}
            required={this.props.required}
            trackingFieldName={this.props.trackingFieldName}
            trackingPopulationLevel={
              this.props.value
                ? InputPopulationLevel.POPULATED_COMPLETE
                : InputPopulationLevel.NOT_POPULATED
            }
            type={this.props.type || 'text'}
            value={this.props.value}
          />
        )}
        {!this.props.trackingFieldName && (
          <LazyInput__DEPRECATED
            autoComplete={this.props.autoCompleteName}
            dataTestId={this.props.dataTestId}
            disabled={this.props.disabled}
            id={this.props.htmlName}
            maxLength={this.props.maxLength || 50}
            name={this.props.htmlName}
            onBlur={this.props.onBlur}
            onChange={this.props.onChange}
            onFocus={this.props.onFocus}
            onKeyPress={this.props.onKeyPress}
            placeholder={this.props.placeholder || this.props.label}
            required={this.props.required}
            type={this.props.type || 'text'}
            value={this.props.value}
          />
        )}
        {this.props.tooltip && (
          <Svg
            className={styles.infoIcon}
            svg={infoIcon}
            {...getTooltipProps({
              type: TooltipType.TEXT,
              tooltip: this.props.tooltip,
              timeoutDelay: 300,
            })}
          />
        )}
      </div>
    )
  }
}

export function Label(props: React.HTMLProps<HTMLLabelElement>) {
  const className = `${styles.label} ${props.className || ''}`
  return <label {...props} className={className} />
}

export class BigSelect extends React.Component<React.HTMLProps<HTMLSelectElement>, {}> {
  public static displayName = 'BigSelect'

  public override render() {
    const className = `${styles.bigSelect} ${this.props.className || ''}`
    const props = { ...this.props, className }
    return <select {...props} />
  }
}

export function Spacing(props: { direction?: 'x' | 'y'; value?: number; multiple?: number }) {
  const spacingValue = props.multiple ? 8 * props.multiple : props.value ? props.value : 0

  return props.direction === 'x' ? (
    <div
      {...stylex.props(styleXStyles.spacing, styleXStyles.horizontalSpacing(spacingValue))}
      aria-hidden="true"
    />
  ) : (
    <div
      {...stylex.props(styleXStyles.spacing, styleXStyles.verticalSpacing(spacingValue))}
      aria-hidden="true"
    />
  )
}
Spacing.displayName = 'Spacing'

// This is here because a disallowed dependency cycle was created
// when exporting directly from the svg.tsx
export const SvgTracked = asTrackedClickable(Svg)

export const AnchorElementTracked = asTrackedClickable(
  (props: React.HTMLProps<HTMLAnchorElement>) => <a {...props}>{props.children}</a>,
)

const styleXStyles = stylex.create({
  spacing: { flexShrink: 0 },
  horizontalSpacing: (width: number) => ({ width }),
  verticalSpacing: (height: number) => ({ height }),
})
